import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Folder, FolderPlus, FolderOpen, FileText, FileImage, FileSpreadsheet,
  FileArchive, Video, File, Upload, UploadCloud, ChevronRight, ChevronDown,
  Eye, Send, MoreVertical, Pencil, Trash2, Download, FolderInput, X, CheckSquare, Square,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import FileViewer from "./FileViewer";
import { openWhatsApp, openWhatsAppWindow, fileMessage, multiFileMessage, folderMessage } from "@/lib/whatsapp";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const IMG = ["jpg", "jpeg", "png", "gif", "webp"];
const VID = ["mp4", "mov", "webm"];
const OFFICE = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
const MAX = 25 * 1024 * 1024;
const isMobile = () => (typeof window !== "undefined" ? window.innerWidth < 768 : false);
const extOf = (f) => (f?.original_filename || "").split(".").pop().toLowerCase();
const kindForExt = (ext) => (IMG.includes(ext) || VID.includes(ext) ? "media" : "document");

function FileIcon({ name, className = "h-4 w-4" }) {
  const ext = (name || "").split(".").pop().toLowerCase();
  if (IMG.includes(ext)) return <FileImage className={`${className} text-violet-500`} />;
  if (VID.includes(ext)) return <Video className={`${className} text-rose-500`} />;
  if (ext === "pdf") return <FileText className={`${className} text-red-500`} />;
  if (["xls", "xlsx", "csv"].includes(ext)) return <FileSpreadsheet className={`${className} text-emerald-600`} />;
  if (ext === "zip") return <FileArchive className={`${className} text-amber-600`} />;
  return <File className={`${className} text-slate-400`} />;
}

export default function FileBrowser({ assetId, asset, shareAsset, canShare, isAdmin, userId }) {
  const qc = useQueryClient();
  const [mobile, setMobile] = useState(isMobile());
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const { data: folders = [] } = useQuery({
    queryKey: ["asset-folders", assetId],
    queryFn: async () => (await api.get(`/assets/${assetId}/folders`)).data,
  });
  const { data: files = [], isSuccess: filesLoaded } = useQuery({
    queryKey: ["asset-files", assetId],
    queryFn: async () => (await api.get(`/assets/${assetId}/files`)).data,
  });

  const roots = useMemo(() => folders.filter((f) => !f.parent_folder_id), [folders]);
  const subsByParent = useMemo(() => {
    const m = {};
    folders.forEach((f) => { if (f.parent_folder_id) (m[f.parent_folder_id] ||= []).push(f); });
    return m;
  }, [folders]);
  const filesByFolder = useMemo(() => {
    const m = { __none__: [] };
    files.forEach((f) => { const k = f.folder_id || "__none__"; (m[k] ||= []).push(f); });
    return m;
  }, [files]);
  const unorganized = filesByFolder.__none__ || [];

  const [expanded, setExpanded] = useState({});
  const [unorgOpen, setUnorgOpen] = useState(true);
  // On first load, keep Unorganized expanded when it has files; collapse only if genuinely empty.
  const unorgInit = useRef(false);
  useEffect(() => {
    if (filesLoaded && !unorgInit.current) {
      unorgInit.current = true;
      setUnorgOpen(unorganized.length > 0);
    }
  }, [filesLoaded, unorganized.length]);
  const isExpanded = (id) => expanded[id] !== false; // default expanded
  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: p[id] === false ? true : false }));

  // ---- viewer / lightbox ----
  const [viewerFile, setViewerFile] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, slides: [], index: 0 });

  const downloadFile = async (f) => {
    const res = await api.get(`/assets/${assetId}/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = f.original_filename; a.click();
    URL.revokeObjectURL(url);
  };

  const openFile = async (f, siblings) => {
    const ext = extOf(f);
    if ((f.size || 0) > MAX && !IMG.includes(ext) && !VID.includes(ext)) {
      toast.message("File too large to preview — downloading instead");
      return downloadFile(f);
    }
    if (IMG.includes(ext)) {
      const imgs = (siblings || []).filter((x) => IMG.includes(extOf(x)));
      const slides = imgs.map((im) => ({ src: `${BACKEND}/api/assets/${assetId}/files/${im.id}/view`, title: im.document_name || im.original_filename }));
      const index = Math.max(0, imgs.findIndex((im) => im.id === f.id));
      return setLightbox({ open: true, slides, index });
    }
    if (VID.includes(ext) || ext === "pdf") return setViewerFile(f);
    if (OFFICE.includes(ext)) {
      if (!canShare) { toast.message("Preview needs a share link — downloading instead"); return downloadFile(f); }
      const w = window.open("", "_blank");
      try {
        const res = await api.post(`/assets/${assetId}/share-link`, { file_ids: [f.id] });
        const url = res.data?.links?.[0]?.url;
        qc.invalidateQueries({ queryKey: ["asset-share-links", assetId] });
        const office = "https://view.officeapps.live.com/op/embed.aspx?src=" + encodeURIComponent(url);
        if (w) w.location.href = office; else window.open(office, "_blank");
      } catch (e) { if (w) w.close(); toast.error("Could not open preview"); }
      return;
    }
    toast.message("No preview for this file type — downloading instead");
    return downloadFile(f);
  };

  // ---- WhatsApp share ----
  const shareOneFile = async (f) => {
    const w = window.open("", "_blank");
    try {
      const res = await api.post(`/assets/${assetId}/share-link`, { file_ids: [f.id] });
      const l = res.data?.links?.[0];
      qc.invalidateQueries({ queryKey: ["asset-share-links", assetId] });
      openWhatsAppWindow(w, fileMessage(shareAsset, l.file_name, l.url, IMG.includes(extOf(f)) || VID.includes(extOf(f))));
    } catch (e) { if (w) w.close(); toast.error("Share failed"); }
  };

  const shareFolder = async (folder) => {
    const w = window.open("", "_blank");
    try {
      const res = await api.post(`/assets/${assetId}/share-folder-link`, { folder_id: folder.folder_id });
      qc.invalidateQueries({ queryKey: ["asset-share-links", assetId] });
      openWhatsAppWindow(w, folderMessage(shareAsset, res.data.folder_name, res.data.url, res.data.file_count));
    } catch (e) { if (w) w.close(); toast.error("Folder share failed"); }
  };

  // ---- multi-select ----
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState({});
  const selectedList = Object.values(selected);
  const toggleSelect = (f) => setSelected((p) => {
    const n = { ...p }; if (n[f.id]) delete n[f.id]; else n[f.id] = { id: f.id, file_name: f.original_filename }; return n;
  });
  const shareSelected = async () => {
    const w = window.open("", "_blank");
    try {
      const ids = selectedList.map((x) => x.id).slice(0, 10);
      const res = await api.post(`/assets/${assetId}/share-link`, { file_ids: ids });
      const links = res.data?.links || [];
      qc.invalidateQueries({ queryKey: ["asset-share-links", assetId] });
      openWhatsAppWindow(w, links.length > 1 ? multiFileMessage(shareAsset, links) : fileMessage(shareAsset, links[0].file_name, links[0].url, false));
      setSelectMode(false); setSelected({});
    } catch (e) { if (w) w.close(); toast.error("Share failed"); }
  };

  // ---- folder + file mutations ----
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["asset-folders", assetId] }); qc.invalidateQueries({ queryKey: ["asset-files", assetId] }); };
  const createFolder = async (name, parent) => {
    try { await api.post(`/assets/${assetId}/folders`, { name, parent_folder_id: parent || null }); invalidate(); toast.success("Folder created"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create folder"); }
  };
  const renameFolder = async (folder_id, name) => {
    try { await api.patch(`/assets/${assetId}/folders/${folder_id}`, { name }); invalidate(); toast.success("Renamed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };
  const deleteFolder = async (folder_id) => {
    try { await api.delete(`/assets/${assetId}/folders/${folder_id}`); invalidate(); toast.success("Folder deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const renameFile = async (file_id, document_name) => {
    try { await api.patch(`/assets/${assetId}/files/${file_id}`, { document_name }); invalidate(); toast.success("Renamed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };
  const moveFile = async (file_id, folder_id) => {
    try { await api.patch(`/assets/${assetId}/files/${file_id}`, { folder_id: folder_id || null }); invalidate(); toast.success("Moved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
  };
  const deleteFile = async (file_id) => {
    if (!window.confirm("Delete this file?")) return;
    try { await api.delete(`/assets/${assetId}/files/${file_id}`); invalidate(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // ---- uploads ----
  const [progress, setProgress] = useState(null); // {done,total,label}
  const uploadOne = async (file, folder_id) => {
    if (file.size > MAX) { toast.error(`${file.name} exceeds 25MB — skipped`); return false; }
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const fd = new FormData();
    fd.append("file", file); fd.append("file_kind", kindForExt(ext));
    if (folder_id) fd.append("folder_id", folder_id);
    try { await api.post(`/assets/${assetId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); return true; }
    catch (e) { toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`); return false; }
  };

  const [pendingUpload, setPendingUpload] = useState(null); // {files:[], targetFolder} for "Save to folder" picker
  const doUploadFiles = async (fileList, folder_id) => {
    const arr = Array.from(fileList).slice(0, 20);
    if (arr.length === 0) return;
    const total = arr.reduce((s, f) => s + (f.size || 0), 0);
    if (total > 200 * 1024 * 1024) toast.message("Large upload — this may take a while");
    setProgress({ done: 0, total: arr.length, label: "Uploading files" });
    for (let i = 0; i < arr.length; i++) { await uploadOne(arr[i], folder_id); setProgress({ done: i + 1, total: arr.length, label: "Uploading files" }); }
    setProgress(null); invalidate();
    // Auto-expand the destination so the just-uploaded file is immediately visible.
    if (folder_id) setExpanded((p) => ({ ...p, [folder_id]: true }));
    else setUnorgOpen(true);
    toast.success(`Uploaded ${arr.length} file(s)`);
  };

  // folder upload (webkitdirectory)
  const [folderPlan, setFolderPlan] = useState(null); // {rootName, buckets:{__root__:[File], subName:[File]}, count, subCount, flattened}
  const onFolderPicked = (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    const rootName = (arr[0].webkitRelativePath || arr[0].name).split("/")[0] || "Uploaded Folder";
    const buckets = { __root__: [] };
    let flattened = false;
    arr.forEach((file) => {
      const segs = (file.webkitRelativePath || file.name).split("/");
      // segs[0]=rootName, last=filename
      if (segs.length <= 2) buckets.__root__.push(file);
      else {
        const sub = segs[1];
        (buckets[sub] ||= []).push(file);
        if (segs.length > 3) flattened = true;
      }
    });
    const subCount = Object.keys(buckets).filter((k) => k !== "__root__").length;
    setFolderPlan({ rootName, buckets, count: arr.length, subCount, flattened });
  };
  const runFolderUpload = async () => {
    const plan = folderPlan; setFolderPlan(null);
    if (!plan) return;
    setProgress({ done: 0, total: plan.count, label: `Uploading ${plan.rootName}` });
    let done = 0;
    try {
      const rootRes = await api.post(`/assets/${assetId}/folders`, { name: plan.rootName, parent_folder_id: null }).catch(async (e) => {
        if (e?.response?.status === 409) { const list = (await api.get(`/assets/${assetId}/folders`)).data; return { data: list.find((f) => !f.parent_folder_id && f.name === plan.rootName) }; }
        throw e;
      });
      const rootId = rootRes.data.folder_id;
      for (const f of plan.buckets.__root__) { await uploadOne(f, rootId); setProgress({ done: ++done, total: plan.count, label: `Uploading ${plan.rootName}` }); }
      for (const sub of Object.keys(plan.buckets).filter((k) => k !== "__root__")) {
        let subId;
        const subRes = await api.post(`/assets/${assetId}/folders`, { name: sub, parent_folder_id: rootId }).catch(async (e) => {
          if (e?.response?.status === 409) { const list = (await api.get(`/assets/${assetId}/folders`)).data; return { data: list.find((f) => f.parent_folder_id === rootId && f.name === sub) }; }
          throw e;
        });
        subId = subRes.data.folder_id;
        for (const f of plan.buckets[sub]) { await uploadOne(f, subId); setProgress({ done: ++done, total: plan.count, label: `Uploading ${sub}` }); }
      }
      if (plan.flattened) toast.message("Folders deeper than 2 levels were flattened into their parent folder");
      toast.success(`Uploaded ${plan.rootName}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Folder upload failed"); }
    setProgress(null); invalidate();
  };

  // dialogs
  const [newFolder, setNewFolder] = useState(null); // {parent} | null
  const [newFolderName, setNewFolderName] = useState("");
  const [renameTarget, setRenameTarget] = useState(null); // {type:'folder'|'file', id, name}
  const [renameVal, setRenameVal] = useState("");
  const [moveTarget, setMoveTarget] = useState(null); // file
  const filesInput = useRef(null);
  const folderInput = useRef(null);

  const folderOptions = useMemo(() => {
    const opts = [{ id: null, label: "Unorganized" }];
    roots.forEach((r) => {
      opts.push({ id: r.folder_id, label: r.name });
      (subsByParent[r.folder_id] || []).forEach((s) => opts.push({ id: s.folder_id, label: `— ${s.name}` }));
    });
    return opts;
  }, [roots, subsByParent]);

  const FileRow = ({ f, siblings }) => (
    <div className="flex items-center gap-2.5 py-2 pl-2 hover:bg-slate-50 rounded" data-testid={`fb-file-${f.id}`}>
      {selectMode && (
        <button type="button" onClick={() => toggleSelect(f)} className="min-h-[44px] min-w-[44px] flex items-center justify-center" data-testid={`fb-file-select-${f.id}`}>
          {selected[f.id] ? <CheckSquare className="h-5 w-5 text-[#B8935A]" /> : <Square className="h-5 w-5 text-slate-400" />}
        </button>
      )}
      <FileIcon name={f.original_filename} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-900 truncate" data-testid={`fb-file-name-${f.id}`}>{f.document_name || f.original_filename}</div>
        <div className="text-[11px] text-slate-400">{f.original_filename} · {((f.size || 0) / 1024).toFixed(0)} KB</div>
      </div>
      {!selectMode && (
        <div className="flex items-center gap-1.5 shrink-0">
          <Button size="sm" variant="outline" className="min-h-[40px]" onClick={() => openFile(f, siblings)} data-testid={`fb-file-view-${f.id}`}>
            <Eye className="h-3.5 w-3.5 mr-1" /> View
          </Button>
          {canShare && (
            <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 min-h-[40px] min-w-[40px]" onClick={() => shareOneFile(f)} data-testid={`fb-file-share-${f.id}`}>
              <Send className="h-3.5 w-3.5" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="min-h-[40px] min-w-[40px] px-0" data-testid={`fb-file-menu-${f.id}`}><MoreVertical className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => downloadFile(f)} data-testid={`fb-file-download-${f.id}`}><Download className="h-3.5 w-3.5 mr-2" /> Download</DropdownMenuItem>
              {canShare && <DropdownMenuItem onClick={() => { setRenameTarget({ type: "file", id: f.id }); setRenameVal(f.document_name || f.original_filename); }} data-testid={`fb-file-rename-${f.id}`}><Pencil className="h-3.5 w-3.5 mr-2" /> Rename</DropdownMenuItem>}
              {canShare && <DropdownMenuItem onClick={() => setMoveTarget(f)} data-testid={`fb-file-move-${f.id}`}><FolderInput className="h-3.5 w-3.5 mr-2" /> Move to folder</DropdownMenuItem>}
              {(isAdmin || (canShare)) && <DropdownMenuItem className="text-red-600" onClick={() => deleteFile(f.id)} data-testid={`fb-file-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );

  const FolderControls = ({ folder, isRoot }) => (
    <div className="flex items-center gap-1.5 shrink-0">
      {isRoot && (
        <Button size="sm" variant="ghost" className="text-xs text-slate-600 min-h-[40px]" onClick={() => { setNewFolder({ parent: folder.folder_id }); setNewFolderName(""); }} data-testid={`fb-add-subfolder-${folder.folder_id}`}>
          <FolderPlus className="h-3.5 w-3.5 mr-1" /> Subfolder
        </Button>
      )}
      {canShare && (
        <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 min-h-[40px] min-w-[40px]" onClick={() => shareFolder(folder)} data-testid={`fb-folder-share-${folder.folder_id}`}>
          <Send className="h-3.5 w-3.5" />
        </Button>
      )}
      {canShare && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="min-h-[40px] min-w-[40px] px-0" data-testid={`fb-folder-menu-${folder.folder_id}`}><MoreVertical className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => { setRenameTarget({ type: "folder", id: folder.folder_id }); setRenameVal(folder.name); }} data-testid={`fb-folder-rename-${folder.folder_id}`}><Pencil className="h-3.5 w-3.5 mr-2" /> Rename</DropdownMenuItem>
            <DropdownMenuItem className="text-red-600" onClick={() => deleteFolder(folder.folder_id)} data-testid={`fb-folder-delete-${folder.folder_id}`}><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  const renderFolder = (folder, isRoot) => {
    const own = filesByFolder[folder.folder_id] || [];
    const subs = isRoot ? (subsByParent[folder.folder_id] || []) : [];
    const open = isExpanded(folder.folder_id);
    return (
      <div key={folder.folder_id} className="mb-1" data-testid={`fb-folder-${folder.folder_id}`}>
        <div className={`flex items-center gap-2 py-2 ${isRoot ? "" : "pl-6"}`}>
          <button type="button" onClick={() => toggle(folder.folder_id)} className="flex items-center gap-1.5 min-w-0 flex-1 text-left min-h-[40px]" data-testid={`fb-folder-toggle-${folder.folder_id}`}>
            {open ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
            {open ? <FolderOpen className="h-4 w-4 text-[#B8935A] shrink-0" /> : <Folder className="h-4 w-4 text-[#B8935A] shrink-0" />}
            <span className="text-sm font-semibold text-slate-900 truncate">{folder.name}</span>
            <span className="text-[11px] text-slate-400">({own.length})</span>
          </button>
          <FolderControls folder={folder} isRoot={isRoot} />
        </div>
        {open && (
          <div className={isRoot ? "pl-6" : "pl-10"}>
            {own.map((f) => <FileRow key={f.id} f={f} siblings={own} />)}
            {subs.map((s) => renderFolder(s, false))}
            {own.length === 0 && subs.length === 0 && <div className="text-xs text-slate-400 py-2 pl-2">Empty folder</div>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-md p-5" data-testid="file-browser">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="label-eyebrow">File Browser</div>
        <div className="flex flex-wrap items-center gap-2">
          {canShare && (files.length > 0) && (
            <Button size="sm" variant="ghost" className="text-emerald-700 min-h-[40px]" onClick={() => { setSelectMode((v) => !v); setSelected({}); }} data-testid="fb-select-toggle">
              <CheckSquare className="h-3.5 w-3.5 mr-1" /> {selectMode ? "Done" : "Select to share"}
            </Button>
          )}
          <Button size="sm" variant="outline" className="min-h-[40px]" onClick={() => { setNewFolder({ parent: null }); setNewFolderName(""); }} data-testid="fb-new-folder-btn">
            <FolderPlus className="h-4 w-4 mr-1.5" /> New Folder
          </Button>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 min-h-[40px]" onClick={() => filesInput.current?.click()} data-testid="fb-upload-files-btn">
            <Upload className="h-4 w-4 mr-1.5" /> Upload Files
          </Button>
          {!mobile && (
            <Button size="sm" variant="outline" className="min-h-[40px]" onClick={() => folderInput.current?.click()} data-testid="fb-upload-folder-btn">
              <UploadCloud className="h-4 w-4 mr-1.5" /> Upload Folder
            </Button>
          )}
          <input ref={filesInput} type="file" multiple className="hidden"
            onChange={(e) => { const fl = Array.from(e.target.files || []); if (fl.length) setPendingUpload({ files: fl }); e.target.value = ""; }} data-testid="fb-files-input" />
          <input ref={folderInput} type="file" webkitdirectory="" directory="" multiple className="hidden"
            onChange={(e) => { onFolderPicked(e.target.files); e.target.value = ""; }} data-testid="fb-folder-input" />
        </div>
      </div>
      <div className="text-xs text-slate-500 mt-0.5">Organize documents & media into folders · max 25MB per file</div>

      {progress && (
        <div className="mt-4" data-testid="fb-upload-progress">
          <div className="text-xs text-slate-600 mb-1">{progress.label} — {progress.done}/{progress.total}</div>
          <Progress value={(progress.done / Math.max(1, progress.total)) * 100} />
        </div>
      )}

      <div className="mt-4">
        {roots.length === 0 && unorganized.length === 0 && (
          <div className="text-sm text-slate-500 py-6 text-center">No files yet. Create a folder or upload files to get started.</div>
        )}
        {roots.map((r) => renderFolder(r, true))}
      </div>

      {/* Unorganized — always visible; collapsed by default when a named folder exists */}
      <div className="mt-3 border-t border-slate-100 pt-2" data-testid="fb-unorganized">
        <button type="button" onClick={() => setUnorgOpen((v) => !v)} className="flex items-center gap-1.5 min-h-[40px] w-full text-left" data-testid="fb-unorganized-toggle">
          {unorgOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          <FolderOpen className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-semibold text-slate-700">Unorganized</span>
          <span className="text-[11px] text-slate-400">({unorganized.length})</span>
        </button>
        {unorgOpen && (
          <div className="pl-6">
            {unorganized.map((f) => <FileRow key={f.id} f={f} siblings={unorganized} />)}
            {unorganized.length === 0 && <div className="text-xs text-slate-400 py-2 pl-2">No unorganized files</div>}
          </div>
        )}
      </div>

      {/* multi-select bar */}
      {selectMode && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-slate-900 text-white px-4 py-3 flex items-center gap-3 flex-wrap shadow-lg" data-testid="fb-select-bar">
          <span className="text-sm font-semibold" data-testid="fb-selected-count">{selectedList.length} selected</span>
          {selectedList.length > 10 && <span className="text-xs text-amber-300" data-testid="fb-select-limit-warning">Select up to 10 files</span>}
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="ghost" className="text-white hover:bg-slate-800 min-h-[44px]" onClick={() => { setSelectMode(false); setSelected({}); }} data-testid="fb-select-cancel">Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 min-h-[44px]" disabled={selectedList.length === 0 || selectedList.length > 10} onClick={shareSelected} data-testid="fb-share-selected-btn">
              <Send className="h-4 w-4 mr-1.5" /> Share {selectedList.length} via WhatsApp
            </Button>
          </div>
        </div>
      )}

      {/* New folder dialog */}
      <Dialog open={!!newFolder} onOpenChange={(o) => { if (!o) setNewFolder(null); }}>
        <DialogContent className="max-w-sm" data-testid="fb-new-folder-dialog">
          <DialogHeader>
            <DialogTitle>{newFolder?.parent ? "New Subfolder" : "New Folder"}</DialogTitle>
            <DialogDescription className="sr-only">Create a folder</DialogDescription>
          </DialogHeader>
          <Input autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Folder name" data-testid="fb-new-folder-name"
            onKeyDown={(e) => { if (e.key === "Enter" && newFolderName.trim()) { createFolder(newFolderName.trim(), newFolder.parent); setNewFolder(null); } }} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolder(null)}>Cancel</Button>
            <Button disabled={!newFolderName.trim()} onClick={() => { createFolder(newFolderName.trim(), newFolder.parent); setNewFolder(null); }} data-testid="fb-new-folder-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => { if (!o) setRenameTarget(null); }}>
        <DialogContent className="max-w-sm" data-testid="fb-rename-dialog">
          <DialogHeader>
            <DialogTitle>Rename {renameTarget?.type}</DialogTitle>
            <DialogDescription className="sr-only">Rename</DialogDescription>
          </DialogHeader>
          <Input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)} data-testid="fb-rename-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button disabled={!renameVal.trim()} onClick={() => { if (renameTarget.type === "folder") renameFolder(renameTarget.id, renameVal.trim()); else renameFile(renameTarget.id, renameVal.trim()); setRenameTarget(null); }} data-testid="fb-rename-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move dialog */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => { if (!o) setMoveTarget(null); }}>
        <DialogContent className="max-w-sm" data-testid="fb-move-dialog">
          <DialogHeader>
            <DialogTitle>Move to folder</DialogTitle>
            <DialogDescription className="sr-only">Choose destination folder</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto -mx-1">
            {folderOptions.map((o) => (
              <button key={o.id || "none"} type="button" className="w-full text-left px-3 py-2.5 rounded hover:bg-slate-100 text-sm" data-testid={`fb-move-opt-${o.id || "none"}`}
                onClick={() => { moveFile(moveTarget.id, o.id); setMoveTarget(null); }}>{o.label}</button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload target picker */}
      <Dialog open={!!pendingUpload} onOpenChange={(o) => { if (!o) setPendingUpload(null); }}>
        <DialogContent className="max-w-sm" data-testid="fb-upload-target-dialog">
          <DialogHeader>
            <DialogTitle>Save {pendingUpload?.files?.length} file(s) to</DialogTitle>
            <DialogDescription className="sr-only">Choose destination folder for upload</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto -mx-1">
            {folderOptions.map((o) => (
              <button key={o.id || "none"} type="button" className="w-full text-left px-3 py-2.5 rounded hover:bg-slate-100 text-sm" data-testid={`fb-uptarget-${o.id || "none"}`}
                onClick={() => { const fl = pendingUpload.files; setPendingUpload(null); doUploadFiles(fl, o.id); }}>{o.label}</button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Folder upload confirm */}
      <Dialog open={!!folderPlan} onOpenChange={(o) => { if (!o) setFolderPlan(null); }}>
        <DialogContent className="max-w-md" data-testid="fb-folder-upload-dialog">
          <DialogHeader>
            <DialogTitle>Confirm folder upload</DialogTitle>
            <DialogDescription>
              You're uploading <b>{folderPlan?.rootName}</b> with {folderPlan?.count} files across {folderPlan?.subCount} subfolder(s) — proceed?
            </DialogDescription>
          </DialogHeader>
          {folderPlan?.flattened && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">Folders deeper than 2 levels will be flattened into their parent folder.</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderPlan(null)}>Cancel</Button>
            <Button onClick={runFolderUpload} data-testid="fb-folder-upload-confirm">Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {viewerFile && (
        <FileViewer file={viewerFile} viewUrl={`${BACKEND}/api/assets/${assetId}/files/${viewerFile.id}/view`} onClose={() => setViewerFile(null)} />
      )}
      <Lightbox open={lightbox.open} close={() => setLightbox((s) => ({ ...s, open: false }))} slides={lightbox.slides} index={lightbox.index} plugins={[Zoom]} />
    </div>
  );
}
